/**
 * Shared line-visibility classifier used by:
 * - editor snapshot producer (`usePagination.utils.js`)
 * - widow/orphan visual control (`widowOrphan.js`)
 *
 * Naming note:
 * - `classifyLineVisibilityAtCut` is the live-DOM variant (this file).
 * - `classifyLineVisibilityAtCutInIframe` is the print variant in
 *   `src/hooks/usePrintDocumentV2.utils.js`.
 *
 * Purity:
 * - `createEmptyLineVisibility` / `classifyVisibilityFromLineGeometry` are pure
 * - geometry readers are impure (DOM reads + optional cache writes)
 */

/**
 * Create an empty line-visibility result shape.
 * Purity: pure.
 */
const createEmptyLineVisibility = (below = false) => ({
  above: false,
  below,
  aboveLineCount: 0,
  belowLineCount: 0,
  totalLineCount: 0,
  allLineBottoms: [],
  aboveLineBottoms: [],
  belowLineBottoms: [],
});

/**
 * Measure text line geometry for an element using DOM ranges.
 * Purity: impure (reads live layout and DOM text nodes).
 */
const measureLineGeometry = (el) => {
  if (!el) return null;
  const baseRect = el.getBoundingClientRect();
  if (!baseRect || baseRect.height <= 0) {
    return {baseHeight: 0, lines: [], allLineBottoms: []};
  }

  const lineBounds = new Map();
  const textNodes = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT);
  while (walker.nextNode()) {
    const tn = walker.currentNode;
    if ((tn.nodeValue || '').trim()) textNodes.push(tn);
  }
  const range = document.createRange();
  for (let i = 0; i < textNodes.length; i++) {
    range.selectNodeContents(textNodes[i]);
    const rects = Array.from(range.getClientRects());
    for (let j = 0; j < rects.length; j++) {
      const r = rects[j];
      if (!r || r.height === 0) continue;
      const relTop = r.top - baseRect.top;
      const relBottom = r.bottom - baseRect.top;
      const lineKey = `${Math.round(relTop * 10)}:${Math.round(relBottom * 10)}`;
      if (!lineBounds.has(lineKey)) {
        lineBounds.set(lineKey, {top: relTop, bottom: relBottom});
      }
    }
  }

  const lines = Array.from(lineBounds.values()).filter(
    (line) => Number.isFinite(line?.top) && Number.isFinite(line?.bottom)
  );
  return {
    baseHeight: baseRect.height,
    lines,
    allLineBottoms: lines.map((line) => line.bottom).sort((a, b) => a - b),
  };
};

/**
 * Fetch cached line geometry or measure and cache it.
 * Purity: impure (reads/writes WeakMap and DOM layout).
 */
const getOrMeasureLineGeometry = (el, lineGeometryCache) => {
  if (!el) return null;
  if (!lineGeometryCache) return measureLineGeometry(el);

  const cached = lineGeometryCache.get(el);
  const isCachedUsable =
    cached &&
    Number.isFinite(cached.baseHeight) &&
    cached.baseHeight > 0 &&
    Array.isArray(cached.lines);
  if (isCachedUsable) return cached;
  if (cached && !isCachedUsable) {
    lineGeometryCache.delete(el);
  }

  const measured = measureLineGeometry(el);
  const isMeasuredUsable =
    measured &&
    Number.isFinite(measured.baseHeight) &&
    measured.baseHeight > 0 &&
    Array.isArray(measured.lines);
  if (isMeasuredUsable) {
    lineGeometryCache.set(el, measured);
  }
  return measured;
};

/**
 * Classify above/below line visibility at a cut height.
 * Purity: pure.
 */
const classifyVisibilityFromLineGeometry = (lineGeometry, cutHeight) => {
  if (!lineGeometry || lineGeometry.baseHeight <= 0) {
    return createEmptyLineVisibility(false);
  }
  if (cutHeight <= 0) {
    return createEmptyLineVisibility(true);
  }

  let above = false;
  let below = false;
  const aboveLineBottoms = [];
  const belowLineBottoms = [];
  const baseHeight = lineGeometry.baseHeight;

  for (let i = 0; i < lineGeometry.lines.length; i++) {
    const line = lineGeometry.lines[i];
    if (line.top >= 0 && line.bottom <= cutHeight) {
      above = true;
      aboveLineBottoms.push(line.bottom);
    }
    if (line.bottom > cutHeight && line.top < baseHeight) {
      below = true;
      belowLineBottoms.push(line.bottom);
    }
  }

  // Geometry fallback for elements with no measurable text rects.
  if (!above && !below) {
    above = cutHeight > 0;
    below = baseHeight > cutHeight;
  }

  return {
    above,
    below,
    aboveLineCount: aboveLineBottoms.length,
    belowLineCount: belowLineBottoms.length,
    totalLineCount: lineGeometry.lines.length,
    allLineBottoms: lineGeometry.allLineBottoms,
    aboveLineBottoms: aboveLineBottoms.sort((a, b) => a - b),
    belowLineBottoms: belowLineBottoms.sort((a, b) => a - b),
  };
};

/**
 * Shared classifier from element + cut to line-visibility counts.
 * Purity: impure (depends on DOM measurement/cache path).
 */
export const classifyLineVisibilityAtCut = (
  el,
  cutHeight,
  lineGeometryCache = null
) => {
  const lineGeometry = getOrMeasureLineGeometry(el, lineGeometryCache);
  if (!lineGeometry || lineGeometry.baseHeight <= 0) {
    return createEmptyLineVisibility(false);
  }
  const result = classifyVisibilityFromLineGeometry(lineGeometry, cutHeight);
  return result;
};
